import { $isMarkNode, $unwrapMarkNode, MarkNode } from '@lexical/mark';
import {
  $createRangeSelection,
  $getRoot, $getSelection,
  $isRangeSelection,
  $isTextNode,
  $setSelection,
  ElementNode,
  PointType,
  RangeSelection,
  TextNode,
} from 'lexical';
import { IMap } from 'typings/DeepIMap';
import { TextAbbreviationDocuments } from 'models';

type GetAbbreviationNode = {
  abbreviationId?: string;
  abbreviationTerm?: string;
};

export const $getAbbreviationSelectionNode = (selection: RangeSelection): MarkNode | null | undefined => {
  const nodes = selection.getNodes();
  const abbreviationNode = nodes.length > 1
    ? nodes.find($isMarkNode)
    : nodes[0].getParents().find($isMarkNode);

  return abbreviationNode && $isMarkNode(abbreviationNode)
    ? abbreviationNode
    : undefined;
};

export const $getAbbreviationData = (): GetAbbreviationNode => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return {};
  }

  const node = $getAbbreviationSelectionNode(selection);

  if (!node) {
    return {
      abbreviationId: undefined,
      abbreviationTerm: selection.getTextContent(),
    };
  }

  const [abbreviationId] = node.getIDs();

  return {
    abbreviationId,
    abbreviationTerm: node.getTextContent(),
  };
};

export const $selectAbbreviation = (abbreviationId: string, abbreviationNumber = 1): boolean => {
  const selection = $createRangeSelection();
  const nodes = $getRoot().getAllTextNodes();
  if (!nodes) {
    return false;
  }

  const abbreviations = nodes.filter((node) => {
    const parent = node.getParents().find($isMarkNode);

    if (!parent) {
      return false;
    }

    return parent.getIDs()[0] === abbreviationId;
  });

  const target = abbreviations[abbreviationNumber - 1];

  if (!target) {
    return false;
  }

  selection.focus.set(target.getKey(), 0, 'text');
  selection.anchor.set(target.getKey(), target.getTextContentSize(), 'text');
  $setSelection(selection);

  return true;
};

export const $removeNodeAbbreviation = (node: MarkNode): void => {
  const [abbreviationId] = node.getIDs();
  node.deleteID(abbreviationId);
  $unwrapMarkNode(node);
};

export const $removeAbbreviationIfTermChanged = (markNode: MarkNode, abbreviationsData: IMap<TextAbbreviationDocuments>): void => {
  const [abbreviationId] = markNode.getIDs();
  const abbreviationTerm = markNode.getTextContent();
  const termBeforeEdit = abbreviationsData.getIn([abbreviationId, 'abbreviation', 'term'], '');
  if (termBeforeEdit.toLowerCase() !== abbreviationTerm.toLowerCase()) {
    $removeNodeAbbreviation(markNode);
  }
};

function findNonEmptyCharIndex(text: string, offset: number, isForward: boolean): number {
  const step = isForward ? 1 : -1;
  let index = offset;
  while (index >= 0 && index < text.length) {
    if (text[index].trim() !== '') {
      return index;
    }
    index += step;
  }

  return -1;
}

function $findNonWhitespaceTextNode(point: PointType, direction: 'forward' | 'backward'): { textNode: TextNode | ElementNode; offset: number } {
  let currentNode: TextNode | ElementNode | null = point.getNode();
  const isForward = direction === 'forward';
  let offset = isForward ? point.offset : point.offset - 1;
  while (currentNode) {
    const textContent = currentNode.getTextContent();
    offset = findNonEmptyCharIndex(textContent, offset, isForward);
    if (offset !== -1) {
      const resultOffset = isForward ? offset : offset + 1;

      return { offset: resultOffset, textNode: currentNode };
    }
    currentNode = isForward ? currentNode.getNextSibling() : currentNode.getPreviousSibling();
    if (!currentNode) {
      break;
    }
    offset = isForward ? 0 : currentNode.getTextContent().length - 1;
  }

  return { offset: point.offset, textNode: point.getNode() };
}

export function $trimSelection(selection: RangeSelection): void {
  const textWithWhiteSpaces = selection.getTextContent();
  const trimmedText = textWithWhiteSpaces.trim();
  if (textWithWhiteSpaces.length === trimmedText.length) {
    return;
  }
  const anchor = selection.anchor;
  const focus = selection.focus;
  const isBackward = selection.isBackward();
  const [startPoint, endPoint] = isBackward ? [focus, anchor] : [anchor, focus];
  const { offset: startOffset, textNode: startTextNode } = $findNonWhitespaceTextNode(startPoint, 'forward');
  const { offset: endOffset, textNode: endTextNode } = $findNonWhitespaceTextNode(endPoint, 'backward');

  if ($isTextNode(startTextNode) && $isTextNode(endTextNode)) {
    selection.setTextNodeRange(startTextNode, startOffset, endTextNode, endOffset);
  }
}
